import React from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { getErrorMessage } from '~libs/axiosWrapper';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, Modal, Spinner } from '~components';
import { FieldProps, Fields } from '~components/form-fields';

import { CarePlanServiceLine } from '~clients/models';

import { useFieldValidationHighlights } from '~clients/hooks/useFieldValidationHighlights';

import {
  useCreateCarePlanServiceLineMutation,
  useGetAppointmentServiceTypesQuery,
  useGetCalendarFrequenciesQuery,
  useGetCarePlanGoalsQuery,
  useUpdateCarePlanServiceLineMutation,
} from '~clients/actions/carePlans';

import { bySequence } from '../reorder';

interface ComponentProps {
  planId: string;
  /** Present → edit mode; absent → create mode. */
  serviceLine?: CarePlanServiceLine;
  /** Preselected goal in create mode (from a goal's "Add Service" action). */
  defaultGoalId?: string | null;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

interface ServiceLineValues {
  [key: string]: string | number | boolean | object | null;
  ext_service_type_id: number | '';
  frequency_id: string;
  goal_id: string;
  notes: string;
  is_active: boolean;
}

const NOTES_PLACEHOLDER = 'e.g. Support worker attends twice weekly to assist with meal preparation';

const validationSchema = Yup.object().shape({
  // Select stores '' when nothing is chosen — treat that as "missing" for the required check.
  ext_service_type_id: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .required('Service type is required'),
  frequency_id: Yup.string().required('Frequency is required'),
  goal_id: Yup.string().required('Linked goal is required'),
});

const ServiceFormModal: React.FC<ComponentProps> = ({ planId, serviceLine, defaultGoalId, isOpen, setIsOpen }) => {
  const { showToast } = useToast();
  const [createServiceLine, { isLoading: isCreating, error: createError, reset: resetCreate }] =
    useCreateCarePlanServiceLineMutation();
  const [updateServiceLine, { isLoading: isUpdating, error: updateError, reset: resetUpdate }] =
    useUpdateCarePlanServiceLineMutation();

  const { data: goals, isLoading: goalsLoading } = useGetCarePlanGoalsQuery({ planId });
  const { data: serviceTypes, isLoading: serviceTypesLoading } = useGetAppointmentServiceTypesQuery();
  const { data: frequencies, isLoading: frequenciesLoading } = useGetCalendarFrequenciesQuery();
  // The selects are built from these lookups — show a loader rather than empty selects
  // until every lookup has resolved.
  const isLoadingLookups = goalsLoading || serviceTypesLoading || frequenciesLoading;

  const { invalidFields, validate } = useFieldValidationHighlights(validationSchema);

  const isEdit = serviceLine !== undefined;
  const isSaving = isCreating || isUpdating;
  // Error from the mutation relevant to this mode (avoids a stale error from the other).
  const error = isEdit ? updateError : createError;

  // The instance is reused across close/reopen for the same line, so a failed attempt's
  // error would otherwise resurface on the next open. Clear both on close.
  React.useEffect(() => {
    if (!isOpen) {
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, resetCreate, resetUpdate]);

  // Goals are offered in display order, numbered to match the Goals tab + By-goal view.
  const goalOptions = bySequence(goals ?? []).map((goal, index) => ({
    id: goal.id,
    name: `${index + 1}. ${goal.description}`,
  }));
  const serviceTypeOptions = (serviceTypes ?? []).map((type) => ({ id: type.id, name: type.name }));
  const frequencyOptions = (frequencies ?? []).map((frequency) => ({ id: frequency.id, name: frequency.name }));

  // Built from live lookup/goal data, so defined in-render.
  const serviceFields: FieldProps = {
    ext_service_type_id: {
      type: 'select',
      caption: 'Service type',
      required: true,
      options: serviceTypeOptions,
      placeholder: '-- Select a service type --',
      dataCy: 'care-plan-service-type',
    },
    frequency_id: {
      type: 'select',
      caption: 'Frequency',
      required: true,
      options: frequencyOptions,
      placeholder: '-- Select a frequency --',
      dataCy: 'care-plan-service-frequency',
    },
    goal_id: {
      type: 'select',
      caption: 'Linked goal',
      required: true,
      options: goalOptions,
      placeholder: '-- Select a goal --',
      dataCy: 'care-plan-service-goal',
    },
    notes: {
      type: 'textarea',
      caption: 'How we will meet this need',
      placeholder: NOTES_PLACEHOLDER,
      dataCy: 'care-plan-service-notes',
    },
    // labels: ['', ''] suppresses the Switch's default YES/NO on/off text.
    is_active: { type: 'switch', caption: 'Active', labels: ['', ''], dataCy: 'care-plan-service-active' },
  };
  const definitions = Object.entries(serviceFields).map(([key, props]) => ({ key, ...props }));

  const initialValues: ServiceLineValues = {
    ext_service_type_id: serviceLine?.ext_service_type_id ?? '',
    frequency_id: serviceLine?.frequency_id ?? '',
    goal_id: serviceLine?.goal_id ?? defaultGoalId ?? '',
    notes: serviceLine?.notes ?? '',
    is_active: serviceLine?.is_active ?? true,
  };

  const onSubmit = async (values: ServiceLineValues) => {
    const body = {
      // Select values may arrive as strings — the API column is an integer FK.
      ext_service_type_id: Number(values.ext_service_type_id),
      frequency_id: values.frequency_id || null,
      goal_id: values.goal_id || null,
      notes: values.notes || null,
      is_active: values.is_active,
    };
    try {
      if (isEdit) {
        await updateServiceLine({ id: serviceLine.id, ...body }).unwrap();
      } else {
        await createServiceLine({ planId, ...body }).unwrap();
      }
      showToast('success', `Service ${isEdit ? 'updated' : 'added'}`);
      setIsOpen(false);
    } catch {
      // Surfaced through the mutation error below.
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={isEdit ? 'Edit Service' : 'Add Service'}
      includeFooter={false}
      bodyClassName="care-plan-form-body"
      size="xl"
    >
      {error !== undefined && <ErrorResponse dataCy="care-plan-service-form-error" message={getErrorMessage(error)} />}
      {isLoadingLookups ? (
        <div className="care-plan-section-loading" data-cy="care-plan-service-form-loading">
          <Spinner loading />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          validate={validate}
          validateOnMount={false}
          validateOnChange={invalidFields.length > 0}
          validateOnBlur={false}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, setFieldTouched, touched, dirty }) => (
            <Form>
              <Fields
                definitions={definitions}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                touched={touched}
                invalidFields={invalidFields}
              />
              <div className="care-plan-service-form-actions">
                <Button type="button" size="sm" color="warning" onClick={() => setIsOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  color="success"
                  disabled={!dirty}
                  loading={isSaving}
                  dataCy="care-plan-service-save"
                >
                  {isEdit ? 'Save' : 'Add'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default ServiceFormModal;
